/**
 * The words of the layouts (document/62, area 3): the frame pages and content
 * types are placed in. Kept apart from dict.ts to keep that file readable; they
 * are spread into the same two trees, so `dict.test.ts` checks them like the rest.
 */
import type { DictNode } from './dict.js'

export const layoutsIt: DictNode = {
  siteLayouts: {
    nav: 'Layout',
    navHelp: 'Le cornici delle pagine',
    title: 'Layout',
    subtitle: 'La cornice in cui stanno le pagine: intestazione, copertina, corpo, colonna laterale e piè di pagina, ciascuna con le sue sezioni di partenza.',
    intro: 'Un layout si pubblica come una pagina: finché non lo pubblichi, i visitatori vedono la versione precedente.',
    list: {
      title: 'Layout del sito',
      none: 'Nessun layout',
      noneHelp: 'Crea il primo layout per dare a più pagine la stessa cornice.',
      select: 'Scegli un layout dall’elenco o creane uno nuovo.',
      regions: '{{count}} regioni',
      sections: '{{count}} sezioni',
      usedByPages: 'usato da {{count}} pagine',
      usedByTypes: '{{count}} tipi di contenuto',
      unused: 'non usato',
      showTrash: 'Cestino',
      hideTrash: 'Torna ai layout',
      loading: 'Carico i layout…',
      errorTitle: 'Non riesco a leggere i layout',
    },
    new: {
      button: 'Nuovo layout',
      title: 'Nuovo layout',
      name: 'Nome',
      nameHelp: 'Come lo riconoscono le persone, per esempio «Pagina con colonna laterale».',
      key: 'Chiave',
      keyHelp: 'Lettere minuscole, numeri e trattini. Non si cambia più.',
      create: 'Crea il layout',
      created: 'Layout creato come bozza.',
      refused: 'Il layout non è stato creato',
    },
    problems: {
      nameMissing: 'Scrivi il nome del layout.',
      keyMissing: 'Scrivi la chiave.',
      keyInvalid: 'Usa lettere minuscole, numeri e trattini, partendo da una lettera.',
      keyTaken: 'Esiste già.',
    },
    status: { draft: 'Bozza', published: 'Pubblicato', trashed: 'Nel cestino' },
    unpublishedChanges: 'Modifiche non pubblicate',
    unpublishedChangesHelp: 'I visitatori vedono ancora la versione pubblicata.',
    editor: {
      back: 'Tutti i layout',
      name: 'Nome del layout',
      nameSave: 'Salva il nome',
      nameSaved: 'Nome salvato.',
      version: 'versione {{version}}',
      trashedBanner: 'Questo layout è nel cestino: ripristinalo per modificarlo.',
      readOnly: 'Puoi leggere i layout ma non modificarli.',
      neverLive: 'Non è ancora online: le pagine che lo scelgono restano senza cornice finché non lo pubblichi.',
      loading: 'Carico il layout…',
    },
    regions: {
      title: 'Regioni',
      subtitle: 'Dall’alto in basso, nell’ordine in cui compaiono. Il corpo di ogni pagina va nella regione «main».',
      add: 'Aggiungi una regione',
      addNamed: 'Aggiungi «{{name}}»',
      customKey: 'Nome della regione',
      customKeyHelp: 'Lettere minuscole, numeri e trattini.',
      customAdd: 'Aggiungi',
      mainName: 'Corpo della pagina',
      mainHelp: 'Qui va il contenuto di ogni pagina che usa il layout. Non ha sezioni di partenza né menu.',
      width: 'Larghezza',
      grid: 'Disposizione',
      menu: 'Menu',
      menuNone: 'Nessun menu',
      menuHelp: 'Il menu del sito mostrato in questa regione.',
      sections: 'Sezioni di partenza',
      sectionsHelp: 'Compaiono in tutte le pagine che usano il layout.',
      noSections: 'Nessuna sezione.',
      addSection: 'Aggiungi sezione',
      sectionKind: 'Tipo di sezione',
      structuredNote: 'Le parti a elenco (bottoni, voci, immagini) si modificano dal compositore di una pagina o da MCP.',
      moveUp: 'Sposta su',
      moveDown: 'Sposta giù',
      remove: 'Rimuovi',
      removeSection: 'Rimuovi la sezione',
      sidebarSide: 'Lato della colonna laterale',
      side: { left: 'A sinistra', right: 'A destra' },
      widths: { narrow: 'Stretta', normal: 'Normale', wide: 'Larga', full: 'Tutta la larghezza' },
      grids: { stack: 'Una sotto l’altra', 'columns-2': 'Due colonne', 'columns-3': 'Tre colonne', 'columns-4': 'Quattro colonne' },
      names: { header: 'Intestazione', hero: 'Copertina', main: 'Corpo', sidebar: 'Colonna laterale', footer: 'Piè di pagina' },
    },
    save: {
      saved: 'Regioni salvate nella bozza.',
      unsaved: 'Modifiche non salvate',
      clean: 'Nessuna modifica',
      discardLocal: 'Annulla',
      submit: 'Salva la bozza',
      saving: 'Salvo…',
    },
    lifecycle: {
      title: 'Pubblicazione',
      subtitle: 'La bozza e la versione online sono separate.',
      current: 'Stato',
      publish: 'Pubblica',
      publishChanges: 'Pubblica le modifiche',
      unpublish: 'Ritira',
      discard: 'Annulla le modifiche',
      trash: 'Sposta nel cestino',
      restore: 'Ripristina',
      help: {
        publish: 'Le pagine che usano il layout lo mostrano alla prossima pubblicazione del sito.',
        unpublish: 'Le pagine che lo usano tornano senza cornice. Non si può se qualcuno lo usa.',
        discard: 'Torni alla versione pubblicata e perdi la bozza.',
        trash: 'Si può ripristinare. Non si può se qualcuno lo usa.',
        restore: 'Torna come bozza, non online.',
      },
      done: {
        publish: 'Layout pubblicato.',
        unpublish: 'Layout ritirato.',
        discard: 'Modifiche annullate.',
        trash: 'Layout spostato nel cestino.',
        restore: 'Layout ripristinato come bozza.',
      },
      unpublishTitle: 'Ritirare il layout?',
      unpublishMessage: 'Le pagine che lo usano tornano senza cornice alla prossima pubblicazione del sito.',
      discardTitle: 'Annullare le modifiche?',
      discardMessage: 'La bozza torna alla versione pubblicata.',
      trashTitle: 'Spostare il layout nel cestino?',
      trashMessage: 'Il layout «{{name}}» si può ripristinare dal cestino.',
      publishBlocked: 'Prima di pubblicare, risolvi questo.',
    },
    usage: {
      title: 'Dove è usato',
      subtitle: 'Pagine e tipi di contenuto che scelgono questo layout.',
      none: 'Nessuno lo usa.',
      pages: 'Pagine',
      types: 'Tipi di contenuto',
      live: 'online',
      inUse: 'Il layout è in uso: scegline un altro (o nessuno) per ciascuna di queste voci, poi riprova.',
      loading: 'Cerco chi lo usa…',
    },
    preview: {
      title: 'Anteprima',
      subtitle: 'Il layout attorno a una pagina di prova, resa dal vero motore delle pagine.',
      working: 'Bozza',
      live: 'Online',
      liveMissing: 'Non è ancora stato pubblicato.',
      frame: 'Anteprima del layout',
      savedHint: 'Salva la bozza per vedere qui le modifiche.',
    },
    pageField: {
      title: 'Layout',
      subtitle: 'La cornice in cui sta il corpo della pagina.',
      label: 'Layout della pagina',
      none: 'Nessun layout (pagina semplice)',
      help: 'La scelta si pubblica con la pagina. Un layout non ancora online non cambia nulla.',
      notLive: 'Il layout «{{name}}» non è online: finché non lo pubblichi la pagina resta senza cornice.',
      manage: 'Gestisci i layout',
      save: 'Salva la scelta',
      saved: 'Layout della pagina salvato.',
      missing: 'La pagina usa il layout «{{key}}», che non esiste più in questo sito.',
    },
    reason: {
      create: 'Layout «{{key}}» creato da Migliore CMS',
      regions: 'Regioni del layout «{{key}}» modificate da Migliore CMS',
      rename: 'Layout «{{key}}» rinominato da Migliore CMS',
      publish: 'Layout «{{key}}» pubblicato da Migliore CMS',
      unpublish: 'Layout «{{key}}» ritirato da Migliore CMS',
      discard: 'Modifiche al layout «{{key}}» annullate da Migliore CMS',
      trash: 'Layout «{{key}}» spostato nel cestino da Migliore CMS',
      restore: 'Layout «{{key}}» ripristinato da Migliore CMS',
      pageLayout: 'Layout della pagina «{{path}}» cambiato da Migliore CMS',
    },
    conflict: {
      title: 'Il layout è stato modificato da qualcun altro',
      message: 'Hai letto la versione {{expectedVersion}}, ora è la {{actualVersion}}.',
      reloaded: 'Layout ricaricato.',
    },
  },
}

export const layoutsEn: DictNode = {
  siteLayouts: {
    nav: 'Layouts',
    navHelp: 'The frames of the pages',
    title: 'Layouts',
    subtitle: 'The frame pages sit in: header, hero, body, sidebar and footer, each with its own default sections.',
    intro: 'A layout is published like a page: until you publish it, visitors keep seeing the previous version.',
    list: {
      title: 'Site layouts',
      none: 'No layouts',
      noneHelp: 'Create the first layout to give several pages the same frame.',
      select: 'Pick a layout from the list or create a new one.',
      regions: '{{count}} regions',
      sections: '{{count}} sections',
      usedByPages: 'used by {{count}} pages',
      usedByTypes: '{{count}} content types',
      unused: 'not used',
      showTrash: 'Trash',
      hideTrash: 'Back to layouts',
      loading: 'Loading layouts…',
      errorTitle: 'Cannot read the layouts',
    },
    new: {
      button: 'New layout',
      title: 'New layout',
      name: 'Name',
      nameHelp: 'What people call it, for example “Page with sidebar”.',
      key: 'Key',
      keyHelp: 'Lowercase letters, digits and hyphens. It never changes.',
      create: 'Create layout',
      created: 'Layout created as a draft.',
      refused: 'The layout was not created',
    },
    problems: {
      nameMissing: 'Write the layout name.',
      keyMissing: 'Write the key.',
      keyInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter.',
      keyTaken: 'It already exists.',
    },
    status: { draft: 'Draft', published: 'Published', trashed: 'In the trash' },
    unpublishedChanges: 'Unpublished changes',
    unpublishedChangesHelp: 'Visitors still see the published version.',
    editor: {
      back: 'All layouts',
      name: 'Layout name',
      nameSave: 'Save the name',
      nameSaved: 'Name saved.',
      version: 'version {{version}}',
      trashedBanner: 'This layout is in the trash: restore it to edit it.',
      readOnly: 'You can read layouts but not change them.',
      neverLive: 'It is not online yet: pages that choose it stay without a frame until you publish it.',
      loading: 'Loading the layout…',
    },
    regions: {
      title: 'Regions',
      subtitle: 'From top to bottom, in the order they appear. The body of every page goes in the “main” region.',
      add: 'Add a region',
      addNamed: 'Add “{{name}}”',
      customKey: 'Region name',
      customKeyHelp: 'Lowercase letters, digits and hyphens.',
      customAdd: 'Add',
      mainName: 'Page body',
      mainHelp: 'The content of every page that uses the layout goes here. It has no default sections and no menu.',
      width: 'Width',
      grid: 'Arrangement',
      menu: 'Menu',
      menuNone: 'No menu',
      menuHelp: 'The site menu shown in this region.',
      sections: 'Default sections',
      sectionsHelp: 'They appear on every page that uses the layout.',
      noSections: 'No sections.',
      addSection: 'Add section',
      sectionKind: 'Section type',
      structuredNote: 'List parts (buttons, items, images) are edited from a page composer or through MCP.',
      moveUp: 'Move up',
      moveDown: 'Move down',
      remove: 'Remove',
      removeSection: 'Remove the section',
      sidebarSide: 'Side of the sidebar',
      side: { left: 'On the left', right: 'On the right' },
      widths: { narrow: 'Narrow', normal: 'Normal', wide: 'Wide', full: 'Full width' },
      grids: { stack: 'One below the other', 'columns-2': 'Two columns', 'columns-3': 'Three columns', 'columns-4': 'Four columns' },
      names: { header: 'Header', hero: 'Hero', main: 'Body', sidebar: 'Sidebar', footer: 'Footer' },
    },
    save: {
      saved: 'Regions saved to the draft.',
      unsaved: 'Unsaved changes',
      clean: 'No changes',
      discardLocal: 'Undo',
      submit: 'Save the draft',
      saving: 'Saving…',
    },
    lifecycle: {
      title: 'Publishing',
      subtitle: 'The draft and the online version are separate.',
      current: 'State',
      publish: 'Publish',
      publishChanges: 'Publish changes',
      unpublish: 'Withdraw',
      discard: 'Discard changes',
      trash: 'Move to trash',
      restore: 'Restore',
      help: {
        publish: 'Pages that use the layout show it from the next site publication.',
        unpublish: 'Pages that use it go back to no frame. Not possible while someone uses it.',
        discard: 'You go back to the published version and lose the draft.',
        trash: 'It can be restored. Not possible while someone uses it.',
        restore: 'It comes back as a draft, not online.',
      },
      done: {
        publish: 'Layout published.',
        unpublish: 'Layout withdrawn.',
        discard: 'Changes discarded.',
        trash: 'Layout moved to the trash.',
        restore: 'Layout restored as a draft.',
      },
      unpublishTitle: 'Withdraw the layout?',
      unpublishMessage: 'Pages that use it go back to no frame at the next site publication.',
      discardTitle: 'Discard the changes?',
      discardMessage: 'The draft goes back to the published version.',
      trashTitle: 'Move the layout to the trash?',
      trashMessage: 'The layout “{{name}}” can be restored from the trash.',
      publishBlocked: 'Before publishing, fix this.',
    },
    usage: {
      title: 'Where it is used',
      subtitle: 'Pages and content types that choose this layout.',
      none: 'Nobody uses it.',
      pages: 'Pages',
      types: 'Content types',
      live: 'online',
      inUse: 'The layout is in use: pick another one (or none) for each of these, then try again.',
      loading: 'Looking for who uses it…',
    },
    preview: {
      title: 'Preview',
      subtitle: 'The layout around a sample page, rendered by the real page engine.',
      working: 'Draft',
      live: 'Online',
      liveMissing: 'It has not been published yet.',
      frame: 'Layout preview',
      savedHint: 'Save the draft to see your changes here.',
    },
    pageField: {
      title: 'Layout',
      subtitle: 'The frame the body of the page sits in.',
      label: 'Page layout',
      none: 'No layout (plain page)',
      help: 'The choice is published with the page. A layout that is not online yet changes nothing.',
      notLive: 'The layout “{{name}}” is not online: until you publish it the page stays without a frame.',
      manage: 'Manage layouts',
      save: 'Save the choice',
      saved: 'Page layout saved.',
      missing: 'The page uses the layout “{{key}}”, which no longer exists on this site.',
    },
    reason: {
      create: 'Layout “{{key}}” created from Migliore CMS',
      regions: 'Regions of layout “{{key}}” changed from Migliore CMS',
      rename: 'Layout “{{key}}” renamed from Migliore CMS',
      publish: 'Layout “{{key}}” published from Migliore CMS',
      unpublish: 'Layout “{{key}}” withdrawn from Migliore CMS',
      discard: 'Changes to layout “{{key}}” discarded from Migliore CMS',
      trash: 'Layout “{{key}}” moved to the trash from Migliore CMS',
      restore: 'Layout “{{key}}” restored from Migliore CMS',
      pageLayout: 'Layout of page “{{path}}” changed from Migliore CMS',
    },
    conflict: {
      title: 'Someone else changed the layout',
      message: 'You read version {{expectedVersion}}, it is now {{actualVersion}}.',
      reloaded: 'Layout reloaded.',
    },
  },
}
